import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col, Tabs, Switch, DatePicker, InputNumber,
} from 'antd'
import { ExportOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'processing',
  APPROVED: 'success',
  ISSUED: 'blue',
  RETURNED: 'cyan',
  CANCELLED: 'error',
}

const UOM_OPTIONS = ['KG', 'NOS', 'MTR', 'SET', 'BOX', 'OTHER']
const CARRIER_OPTIONS = ['PERSON', 'VEHICLE', 'COURIER']
const STATUS_TABS = ['ALL', 'PENDING', 'APPROVED', 'ISSUED', 'RETURNED']

export default function MaterialOutwardPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState('ALL')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()
  const isReturnable = Form.useWatch('is_returnable', form)

  const { data: passes, isLoading } = useQuery({
    queryKey: ['material-outward', plantId, activeTab],
    queryFn: () =>
      apiClient.get(`${API}/material-outward`, {
        params: {
          plant_id: plantId,
          status: activeTab === 'ALL' ? undefined : activeTab,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/material-outward`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-outward'] })
      message.success('Gate pass created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const actionMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiClient.post(`${API}/material-outward/${id}/${action}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-outward'] })
      message.success('Updated')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const list = Array.isArray(passes) ? passes : []

  const columns = [
    {
      title: 'Pass No',
      dataIndex: 'pass_no',
      key: 'pass_no',
      width: 120,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Date',
      dataIndex: 'pass_date',
      key: 'pass_date',
      width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY') : '—',
    },
    { title: 'Dept', dataIndex: 'requesting_dept', key: 'requesting_dept', width: 120 },
    {
      title: 'Item Description',
      dataIndex: 'item_description',
      key: 'item_description',
      ellipsis: true,
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 100,
      render: (_: unknown, r: any) => `${r.quantity} ${r.uom}`,
    },
    {
      title: 'Returnable',
      dataIndex: 'is_returnable',
      key: 'is_returnable',
      width: 100,
      render: (v: boolean) => <Tag color={v ? 'blue' : 'default'}>{v ? 'YES' : 'NO'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: unknown, r: any) => (
        <Space>
          {r.status === 'PENDING' && (
            <Button
              size="small"
              type="primary"
              loading={actionMut.isPending}
              onClick={() => actionMut.mutate({ id: r.id, action: 'approve' })}
            >
              Approve
            </Button>
          )}
          {r.status === 'APPROVED' && (
            <Button
              size="small"
              loading={actionMut.isPending}
              onClick={() => actionMut.mutate({ id: r.id, action: 'issue' })}
            >
              Issue
            </Button>
          )}
          {r.status === 'ISSUED' && r.is_returnable && (
            <Button
              size="small"
              loading={actionMut.isPending}
              onClick={() => actionMut.mutate({ id: r.id, action: 'return' })}
            >
              Return
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ExportOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Material Outward Gate Pass
      </Title>
      <Typography.Text type="secondary">Manage material outward passes</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 200 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setDrawerOpen(true) }}>
              Create Pass
            </Button>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={STATUS_TABS.map(s => ({ key: s, label: s }))}
        />

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Drawer
        title="Create Material Outward Pass"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={500}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending}
                onClick={() =>
                  form.validateFields().then(vals => {
                    const payload = {
                      ...vals,
                      plant_id: plantId,
                      expected_return_date: vals.expected_return_date
                        ? vals.expected_return_date.format('YYYY-MM-DD')
                        : undefined,
                    }
                    createMut.mutate(payload)
                  })
                }
              >
                Create
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical" initialValues={{ is_returnable: false }}>
          <Form.Item name="requesting_dept" label="Requesting Department" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="item_description" label="Item Description" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="item_code" label="Item Code">
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="uom" label="UOM" rules={[{ required: true }]}>
                <Select options={UOM_OPTIONS.map(u => ({ value: u, label: u }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="reason" label="Reason">
            <Input />
          </Form.Item>
          <Form.Item name="destination" label="Destination">
            <Input />
          </Form.Item>
          <Form.Item name="is_returnable" label="Is Returnable?" valuePropName="checked">
            <Switch />
          </Form.Item>
          {isReturnable && (
            <Form.Item name="expected_return_date" label="Expected Return Date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item name="carrier_type" label="Carrier Type">
            <Select options={CARRIER_OPTIONS.map(c => ({ value: c, label: c }))} />
          </Form.Item>
          <Form.Item name="carrier_details" label="Carrier Details">
            <Input placeholder="Person name / Vehicle no / Courier name" />
          </Form.Item>
          <Form.Item name="serial_nos" label="Serial Nos">
            <Input.TextArea rows={2} placeholder="Comma separated serial numbers" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
